import { IWebsiteBuilderFormItemOptionRaw, IWebsiteBuilderFormItemRaw, IWebsiteBuilderFormRaw, IWebsiteBuilderFormRowRaw } from "redux/reducers/models/websiteBuilder";
import { IFormBuilderForm } from "../../editor/model/formBuilderForm";
import { isFormBuilderButtonItem } from "../../editor/model/item/formBuilderButton";
import { isFormBuilderCheckboxItem } from "../../editor/model/item/formBuilderCheckbox";
import { isFormBuilderDateItem } from "../../editor/model/item/formBuilderDate";
import { isFormBuilderInputItem } from "../../editor/model/item/formBuilderInput";
import { TFormBuilderItem } from "../../editor/model/item/formBuilderItem";
import { isFormBuilderSelectItem } from "../../editor/model/item/formBuilderSelect";
import { isFormBuilderTextItem } from "../../editor/model/item/formBuilderText";
import { isFormBuilderTextareaItem } from "../../editor/model/item/formBuilderTextarea";
import { IFormBuilderRow } from "../../editor/model/formBuilderRow";
import { IFormBuilderItemOption } from "../../editor/model/item/formBuilderItemOption";

function serializeForm(form: IFormBuilderForm): IWebsiteBuilderFormRaw {
  return {
    id: form.id,
    key: form.key,
    website_id: form.websiteId,
    name: form.settings.name,
    email_notification_enabled: form.settings.emailNotificationsEnabled,
    updated_at: form.updatedAt,
    archived_at: form.archivedAt,
    rows: form.children.map((row, index) => serializeRow(row, index + 1)),
  };
}

function serializeRow(row: IFormBuilderRow, position: number): IWebsiteBuilderFormRowRaw {
  return {
    id: row.id,
    form_id: row.data.formId,
    key: row.key,
    position,
    justify_content: row.data.settings.justifyContent,
    align_items: row.data.settings.alignItems,
    items: row.children.map((item, index) => serializeItem(item, index + 1)),
  };
}

interface ISerializeItemSettingsData {
  label: string;
  placeholder: string;
  required: boolean;
  content: string;
  isNumeric: boolean;
  options: IWebsiteBuilderFormItemOptionRaw[];
}

type TSerializeItemSettings = Partial<ISerializeItemSettingsData>;

function serializeItem(item: TFormBuilderItem, position: number): IWebsiteBuilderFormItemRaw {
  const id = item.id;
  const form_row_id = item.data.formRowId;
  const key = item.key;
  const type = item.data.type;
  const role = item.data.role;
  
  const serialize = (settings: TSerializeItemSettings): IWebsiteBuilderFormItemRaw => {
    let label = "";
    let placeholder = "";
    let required = false;
    let content = "";
    let is_numeric = false;
    let options: IWebsiteBuilderFormItemOptionRaw[] = [];
    
    if (settings.label != null) {
      label = settings.label;
    }

    if (settings.placeholder != null) {
      placeholder = settings.placeholder;
    }

    if (settings.required != null) {
      required = settings.required;
    }

    if (settings.content != null) {
      content = settings.content;
    }

    if (settings.isNumeric != null) {
      is_numeric = settings.isNumeric;
    }

    if (settings.options != null) {
      options = settings.options;
    }

    return {
      id,
      form_row_id,
      key,
      type,
      role,
      position,
      label,
      placeholder,
      required,
      content,
      is_numeric,
      options,
    };
  };

  if (isFormBuilderButtonItem(item)) {
    const { label } = item.data.settings;

    return serialize({ label });
  }

  if (isFormBuilderCheckboxItem(item)) {
    const { label } = item.data.settings;

    return serialize({ label });
  }

  if (isFormBuilderDateItem(item)) {
    const { label, required } = item.data.settings;

    return serialize({ label, required });
  }

  if (isFormBuilderInputItem(item)) {
    const { label, placeholder, required, isNumeric } = item.data.settings;

    return serialize({ label, placeholder, required, isNumeric });
  }

  if (isFormBuilderSelectItem(item)) {
    const { label, placeholder, required, options } = item.data.settings;

    return serialize({ label, placeholder, required, options: options.map((option, index) => serializeItemOption(option, index + 1)) });
  }

  if (isFormBuilderTextItem(item)) {
    const { content } = item.data.settings;

    return serialize({ content });
  }

  if (isFormBuilderTextareaItem(item)) {
    const { label, placeholder, required } = item.data.settings;

    return serialize({ label, placeholder, required });
  }

  // eslint-disable-next-line no-unused-vars
  const _exhaustiveCheck: never = item;
  return null;
}

function serializeItemOption(itemOption: IFormBuilderItemOption, position: number): IWebsiteBuilderFormItemOptionRaw {
  return {
    id: itemOption.id,
    form_item_id: itemOption.formItemId,
    position,
    label: itemOption.label,
    value: itemOption.value,
  };
}

export function formBuilderSerializeForm(form: IFormBuilderForm): IWebsiteBuilderFormRaw {
  return serializeForm(form);
}